import { Component, Input, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';
import { DisparoAutomaticoModel } from '../../../../shared/model/disparo-automatico/disparo-automatico.model';
import { DisparoIntegracaoModel } from '../../../../shared/model/disparo-automatico/disparo-integracao.model';
import { IntegracaoVariavelModel } from '../../../../shared/model/integracao-model';
import { IntegracaoService } from '../../../../shared/service/integracao.service';
import { MsgService } from '../../../../shared/service/msg.service';
import { JsonEditorComponent } from '../../../../shared/components/json-editor/json-editor.component';
import { SlideTogglePadraoComponent } from '../../../../shared/components/slide-toggle-padrao/slide-toggle-padrao.component';
import { SelectOneComponent } from '../../../../shared/components/select-one/select-one.component';

interface OpcaoVariavel {
  codigo: string;
  nome: string;
}

@Component({
  selector: 'app-integracao-disparo',
  standalone: true,
  imports: [FormsModule, MatIconModule, JsonEditorComponent, SlideTogglePadraoComponent, SelectOneComponent],
  templateUrl: './integracao-disparo.component.html',
  styleUrl: './integracao-disparo.component.css'
})
export class IntegracaoDisparoComponent implements OnChanges {
  private readonly integracaoService = inject(IntegracaoService);
  private readonly msgService = inject(MsgService);

  @ViewChild(JsonEditorComponent) private editorPayload?: JsonEditorComponent;

  @Input({ required: true }) disparoAutomatico!: DisparoAutomaticoModel;
  @Input() podeEditar = true;

  readonly opcoesVariaveis: OpcaoVariavel[] = [
    { codigo: 'NOME', nome: 'Nome' },
    { codigo: 'CPF', nome: 'CPF/CNPJ' },
    { codigo: 'TELEFONE', nome: 'Telefone' },
    { codigo: 'EMAIL', nome: 'E-mail' }
  ];

  personalizada = false;
  integracoes: DisparoIntegracaoModel[] = [];
  integracaoSelecionada?: DisparoIntegracaoModel;
  carregando = false;
  carregandoDetalhe = false;
  payloadValido = true;
  erroCarregamento?: string;
  private tipoCarregado?: boolean;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['disparoAutomatico'] || !this.disparoAutomatico) return;
    this.personalizada = Boolean(this.disparoAutomatico.integracao?.personalizada);
    this.integracaoSelecionada = this.disparoAutomatico.integracao;

    if (changes['disparoAutomatico'].firstChange || this.tipoCarregado !== this.personalizada) {
      this.carregarIntegracoes(true);
    }
  }

  alterarTipo(padrao: boolean): void {
    const personalizada = !padrao;
    if (this.personalizada === personalizada && this.tipoCarregado === personalizada) return;

    this.personalizada = personalizada;
    this.integracaoSelecionada = undefined;
    this.disparoAutomatico.integracao = new DisparoIntegracaoModel();
    this.disparoAutomatico.integracao.personalizada = personalizada;
    this.disparoAutomatico.integracao.payload = '{}';
    this.carregarIntegracoes();
  }

  selecionarIntegracao(resumo?: DisparoIntegracaoModel): void {
    if (!resumo?.id) {
      this.integracaoSelecionada = undefined;
      const vazia = new DisparoIntegracaoModel();
      vazia.personalizada = this.personalizada;
      this.disparoAutomatico.integracao = vazia;
      return;
    }

    const anterior = this.disparoAutomatico.integracao;
    this.integracaoSelecionada = resumo;
    const pendente = new DisparoIntegracaoModel();
    pendente.personalizada = this.personalizada;
    this.disparoAutomatico.integracao = pendente;
    this.carregandoDetalhe = true;
    this.erroCarregamento = undefined;
    this.integracaoService.buscarIntegracaoParaDisparo(resumo.id)
      .pipe(finalize(() => this.carregandoDetalhe = false))
      .subscribe({
        next: selecionada => {
          const manterPayload = Boolean(
            selecionada.personalizada
            && anterior?.id === selecionada.id
            && anterior?.payload?.trim()
          );
          this.disparoAutomatico.integracao = Object.assign(new DisparoIntegracaoModel(), selecionada, {
            payload: manterPayload ? anterior!.payload : (selecionada.personalizada ? '{}' : selecionada.payload),
            variaveis: manterPayload
              ? [...(anterior!.variaveis ?? [])]
              : [...(selecionada.personalizada ? [] : (selecionada.variaveis ?? []))]
          });
          this.payloadValido = true;
        },
        error: () => {
          const vazia = new DisparoIntegracaoModel();
          vazia.personalizada = this.personalizada;
          this.disparoAutomatico.integracao = vazia;
          this.integracaoSelecionada = undefined;
          this.erroCarregamento = 'Não foi possível carregar os dados da integração selecionada. Tente novamente.';
          this.msgService.msgErro(this.erroCarregamento);
        }
      });
  }

  atualizarPayload(payload: string): void {
    const integracao = this.disparoAutomatico.integracao;
    if (!integracao) return;

    integracao.payload = payload;
    this.sincronizarVariaveisDoPayload(integracao, payload);
  }

  private sincronizarVariaveisDoPayload(integracao: DisparoIntegracaoModel, payload: string): void {
    const ordensExistentes = new Set((integracao.variaveis ?? []).map(variavel => variavel.ordem));
    const novasOrdens = [...new Set(
      [...payload.matchAll(/\{\{(\d+)}}/g)].map(marcador => Number(marcador[1]))
    )].filter(ordem => !ordensExistentes.has(ordem));

    if (!novasOrdens.length) return;

    novasOrdens.sort((a, b) => a - b);
    integracao.variaveis = [
      ...(integracao.variaveis ?? []),
      ...novasOrdens.map(ordem => ({ ordem }))
    ];
  }

  adicionarVariavel(): void {
    const integracao = this.disparoAutomatico.integracao;
    if (!integracao || !this.podeEditar) return;

    const maiorOrdem = (integracao.variaveis ?? []).reduce(
      (maior, item) => Math.max(maior, item.ordem ?? 0), 0);
    const ordem = maiorOrdem + 1;
    const marcador = `{{${ordem}}}`;
    if (!this.editorPayload?.inserirNoCursor(marcador)) {
      this.msgService.msgAdvertencia('Posicione o cursor dentro de um valor textual do JSON para adicionar a variável.');
      return;
    }
    integracao.variaveis = [...(integracao.variaveis ?? []), { ordem }];
  }

  selecionarVariavel(variavel: IntegracaoVariavelModel, opcao?: OpcaoVariavel): void {
    variavel.campo = opcao?.codigo;
  }

  opcaoVariavelSelecionada(campo?: string): OpcaoVariavel | undefined {
    return this.opcoesVariaveis.find(opcao => opcao.codigo === campo);
  }

  removerVariavel(ordem?: number): void {
    const integracao = this.disparoAutomatico.integracao;
    if (!integracao || ordem == null || !this.podeEditar) return;
    const marcador = `{{${ordem}}}`;
    integracao.variaveis = (integracao.variaveis ?? []).filter(item => item.ordem !== ordem);
    integracao.payload = (integracao.payload ?? '').split(marcador).join('');
  }

  montarMarcador(ordem?: number): string {
    return `{{${ordem ?? ''}}}`;
  }

  mensagemAdvertencia(): string | null {
    const integracao = this.disparoAutomatico.integracao;
    if (!integracao?.id) return null;
    if (!integracao.personalizada) return null;
    if (!integracao.payload?.trim()) return 'Informe o payload da integração personalizada.';
    if (!this.payloadValido) return 'Corrija o JSON do payload antes de salvar.';

    try {
      const json = JSON.parse(integracao.payload);
      if (!json || Array.isArray(json) || typeof json !== 'object' || Object.keys(json).length === 0) {
        return 'O payload precisa ser um objeto JSON com ao menos um campo.';
      }
    } catch {
      return 'O payload informado não é um JSON válido.';
    }

    const marcadores = new Set(
      [...integracao.payload.matchAll(/\{\{(\d+)}}/g)].map(item => Number(item[1])));
    const variaveis = integracao.variaveis ?? [];
    if (variaveis.some(item => !item.campo)) return 'Selecione o campo de todas as variáveis do payload.';
    const ordens = new Set(variaveis.map(item => item.ordem));
    if (marcadores.size !== ordens.size || [...marcadores].some(ordem => !ordens.has(ordem))) {
      return 'Cada marcador do payload deve possuir uma variável correspondente.';
    }
    return null;
  }

  private carregarIntegracoes(preservarSelecionada = false): void {
    if (this.carregando && this.tipoCarregado === this.personalizada) return;

    const selecionadaAtual = preservarSelecionada ? this.disparoAutomatico.integracao : undefined;
    this.carregando = true;
    this.erroCarregamento = undefined;
    this.tipoCarregado = this.personalizada;
    this.integracaoService.buscarParaDisparo(this.personalizada)
      .pipe(finalize(() => this.carregando = false))
      .subscribe({
        next: pagina => {
          this.integracoes = pagina.content ?? [];
          if (!preservarSelecionada && this.integracoes.length === 0) {
            this.msgService.msgAdvertencia(
              `Nenhuma integração ${this.personalizada ? 'personalizada' : 'padrão'} ativa foi cadastrada em Parâmetros.`);
          }
          if (selecionadaAtual?.id) {
            const cadastrada = this.integracoes.find(item => item.id === selecionadaAtual.id);
            if (cadastrada) {
              this.integracaoSelecionada = cadastrada;
              this.disparoAutomatico.integracao = Object.assign(
                new DisparoIntegracaoModel(),
                cadastrada,
                selecionadaAtual,
                { variaveis: [...(selecionadaAtual.variaveis ?? [])] });
            }
          }
        },
        error: () => {
          this.integracoes = [];
          this.erroCarregamento = 'Não foi possível carregar as integrações. Tente novamente.';
          this.msgService.msgErro(this.erroCarregamento);
        }
      });
  }
}
